import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, Flag, Package, Columns3, Check } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ExportButton from '../../components/ui/ExportButton';
import { ProvenanceTooltip } from '../../components/ui/ProvenanceTooltip';
import { sourcePrefix, type ProvenanceMap } from '../../components/ui/ProvenanceIndicator';
import { useParamStore } from '../../hooks/useParamStore';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface ChoosableColumn {
  colId: string;
  label: string;
}

const CHOOSABLE_COLUMNS: ChoosableColumn[] = [
  { colId: 'image_url', label: 'Image' },
  { colId: 'title', label: 'Title' },
  { colId: 'brand', label: 'Brand' },
  { colId: 'category_algopix', label: 'Category' },
  { colId: 'upc', label: 'UPC' },
  { colId: 'asin', label: 'ASIN' },
  { colId: 'cost_usd', label: 'Cost' },
  { colId: 'amazon_buy_box_price', label: 'Buy Box' },
  { colId: 'amazon_demand_rating', label: 'Demand' },
  { colId: 'amazon_monthly_sales', label: 'Mo. Sales' },
  { colId: 'amazon_seller_count', label: 'Sellers' },
  { colId: 'assigned_buyer_full_name', label: 'Buyer' },
  { colId: '_flags', label: 'Flagged' },
  // Day 84 P6.3 L11 — manual override badge column. Hidden by default per
  // HIDDEN_BY_DEFAULT. Sources from product_override_counts VIEW via Plan B
  // (b1) hook-level Map fetch after R2 PostgREST embed empirical test
  // returned `null` even with service-key + seeded fixture (VIEW with
  // security_invoker=true is not embeddable via FK; direct view query works).
  { colId: '_overrides', label: 'Overrides' },
  { colId: '_clearance', label: 'Clearance' },
  { colId: 'updated_at', label: 'Updated' },
];

// Columns hidden on first render unless the user has explicitly enabled them
// (localStorage entry overrides). Keeps long-tail or specialty columns out
// of the default layout while still being a single click away.
const HIDDEN_BY_DEFAULT: Record<string, boolean> = {
  assigned_buyer_full_name: true,
  // Day 84 P6.3 L11 — overrides badge column is opt-in (most products won't
  // have an override; default-hidden keeps the grid clean).
  _overrides: true,
  // K5.3 (F1) — clearance badge column is opt-in (most products aren't lots; default-hidden keeps the
  // grid clean + respects the column-width budget, Rule 16). The badge is always visible on
  // ProductDetail (F2); the grid column is one click away via the column toggle.
  _clearance: true,
};

// Product row shape rendered in the grid — mirror of the SELECT list below
// plus the computed _flagCount.
interface ProductRow {
  id: string;
  isg_sku: string | null;
  title: string | null;
  brand: string | null;
  category_algopix: string | null;
  upc: string | null;
  asin: string | null;
  // U10 A1 — junction-assembled at the fetch (primary product_distributors row →
  // cost_usd); the products.cost_usd read-copy select is out (slice-B drop family).
  cost_usd: number | null;
  amazon_buy_box_price: number | null;
  amazon_demand_rating: string | null;
  amazon_monthly_sales: number | null;
  amazon_seller_count: number | null;
  image_url: string | null;
  updated_at: string | null;
  assigned_buyer_user_id: string | null;
  // Flattened from PostgREST embed `assigned_buyer:assigned_buyer_user_id(full_name)`
  // — see SELECT below. Map step builds it from `row.assigned_buyer?.full_name`.
  assigned_buyer_full_name: string | null;
  // Day 83 P6.2 — per-field provenance JSONB sidecar (Day 82 L1 envelope shape).
  // Consumer of grid-visible tracked field cost_usd (others — algopix_data,
  // map_price — not surfaced as columns today). The 'cost_usd' provenance KEY is
  // trigger-maintained from the primary junction (read-copy family; its slice-B
  // fate rides the :317 drop).
  provenance: ProvenanceMap | null;
  // K5.3 (F1) — clearance lot marker + self-FK (added to the SELECT below; drive the badge column).
  is_clearance_lot: boolean | null;
  parent_product_id: string | null;
  _flagCount?: number;
  // Day 84 P6.3 L11 — override counts joined from product_override_counts
  // VIEW via Plan B hook-level Map merge in getRowsFn.
  _velocityOverrideCount?: number;
  _priceOverrideCount?: number;
}

type FlagFilter = 'all' | 'flagged' | 'unflagged';

// ── Products page ───────────────────────────────────────────────────────────

export default function Products(): JSX.Element {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<ProductRow> | null>(null);
  const gridApiRef = useRef<GridApi<ProductRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  const [colChooserOpen, setColChooserOpen] = useState<boolean>(false);

  // Day 83 P6.2 — column-toggle Cost-entry subtitle snapshot per R1 Sol-lock.
  // Computed inside the dropdown-open handler via forEachNodeAfterFilterAndSort
  // (IRM-aware: snapshots only loaded blocks), cached here, cleared on close.
  // null = "no subtitle" (initial / closed / 0 rows loaded).
  const [costSourceSubtitle, setCostSourceSubtitle] = useState<string | null>(null);

  // Day 83 P6.2 — manual_override_stale_days business-level param (Day 82 §2
  // seed at 90). Distributor-overridable via param hierarchy; Products grid is
  // distributor-agnostic so we resolve at business level only. Fallback 90 if
  // param hasn't loaded yet (avoids a render-blocking dep on paramStore.loading).
  const paramStore = useParamStore();
  const staleParam = paramStore.resolveKey('manual_override_stale_days');
  const staleParsed = Number(staleParam?.param.value);
  const staleDays: number = Number.isFinite(staleParsed) ? staleParsed : 90;

  // Column visibility — persisted in localStorage
  const [hiddenCols, setHiddenCols] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('arbi_products_columns');
      const parsed = saved ? (JSON.parse(saved) as Record<string, boolean>) : {};
      // Saved entries override defaults — so a user who explicitly toggled
      // a default-hidden column ON keeps it visible.
      return { ...HIDDEN_BY_DEFAULT, ...parsed };
    } catch {
      return { ...HIDDEN_BY_DEFAULT };
    }
  });

  const toggleColVisibility = useCallback((colId: string): void => {
    setHiddenCols((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      localStorage.setItem('arbi_products_columns', JSON.stringify(next));
      if (gridApiRef.current) {
        gridApiRef.current.setColumnsVisible([colId], !next[colId]);
      }
      return next;
    });
  }, []);

  // Day 83 P6.2 — column-chooser open/close handlers per R1 Sol-lock.
  // Open snapshots aggregated cost-source via forEachNodeAfterFilterAndSort
  // (IRM-aware: iterates currently-loaded blocks only); close clears the
  // snapshot. Single-source → tooltip-prefix per L4; ≥2 → "Mixed sources";
  // 0 loaded rows OR all-null provenance → null (omit subtitle entirely).
  const closeColChooser = useCallback((): void => {
    setColChooserOpen(false);
    setCostSourceSubtitle(null);
  }, []);

  const openColChooser = useCallback((): void => {
    const api = gridApiRef.current;
    if (!api) {
      setCostSourceSubtitle(null);
    } else {
      const sources = new Set<string>();
      let anyLoaded = false;
      api.forEachNodeAfterFilterAndSort((node) => {
        if (!node.data) return;
        anyLoaded = true;
        const src = node.data.provenance?.['cost_usd']?.source;
        if (typeof src === 'string') sources.add(src);
      });
      if (!anyLoaded || sources.size === 0) {
        setCostSourceSubtitle(null);
      } else if (sources.size === 1) {
        const only = sources.values().next().value;
        setCostSourceSubtitle(only ? sourcePrefix(only) : null);
      } else {
        setCostSourceSubtitle('Mixed sources');
      }
    }
    setColChooserOpen(true);
  }, []);

  // Flag counts per product (loaded separately)
  const [flagCounts, setFlagCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    supabase
      .from('product_flags')
      .select('product_id')
      .eq('status', 'open')
      .then(({ data }) => {
        const counts: Record<string, number> = {};
        for (const row of (data ?? []) as { product_id: string }[]) {
          counts[row.product_id] = (counts[row.product_id] || 0) + 1;
        }
        setFlagCounts(counts);
      });
  }, []);

  // State ref for getRows
  const stateRef = useRef<{ searchText: string; flagFilter: FlagFilter }>({
    searchText,
    flagFilter,
  });
  stateRef.current = { searchText, flagFilter };

  const getRowsFn = useCallback(
    async (params: IGetRowsParams): Promise<void> => {
      const { startRow, endRow, sortModel, successCallback, failCallback } = params;
      const { searchText: st } = stateRef.current;

      try {
        let query = supabase
          .from('products')
          .select(
            // U10 cost re-point — the junction embed replaces the products.cost_usd
            // read-copy select (slice-B drop family); primary row assembled below.
            'id, isg_sku, title, brand, category_algopix, upc, asin, amazon_buy_box_price, amazon_demand_rating, amazon_monthly_sales, amazon_seller_count, image_url, updated_at, assigned_buyer_user_id, assigned_buyer:assigned_buyer_user_id(full_name), provenance, is_clearance_lot, parent_product_id, product_distributors ( cost_usd, is_primary )',
            { count: 'exact' }
          )
          .eq('product_distributors.is_primary', true);

        if (st.trim()) {
          const term = `%${st.trim()}%`;
          query = query.or(
            `isg_sku.ilike.${term},title.ilike.${term},brand.ilike.${term},upc.ilike.${term},asin.ilike.${term}`
          );
        }

        if (sortModel.length > 0) {
          const s = sortModel[0];
          query = query.order(s.colId, { ascending: s.sort === 'asc', nullsFirst: false });
        } else {
          query = query.order('isg_sku', { ascending: true });
        }

        query = query.range(startRow, endRow - 1);
        const { data, count, error } = await query;
        if (error) throw error;

        // Flatten the `assigned_buyer` embed into a top-level column so AG
        // Grid can use it directly via field-key. Embed shape is
        // `{ full_name: string | null } | null`.
        type EmbeddedRow = ProductRow & {
          assigned_buyer?: { full_name: string | null } | null;
          product_distributors?: { cost_usd: number | null; is_primary: boolean | null }[] | null;
        };
        const baseRows = ((data ?? []) as unknown as EmbeddedRow[]).map((p) => ({
          ...p,
          assigned_buyer_full_name: p.assigned_buyer?.full_name ?? null,
          // Junction-assemble the effective cost (primary row → cost_usd; no
          // retain-last-known — no primary junction row reads null).
          cost_usd: p.product_distributors?.[0]?.cost_usd ?? null,
          _flagCount: flagCounts[p.id] || 0,
        }));

        // Day 84 P6.3 L11 — Plan B (b1) override counts merge. R2 PostgREST
        // embed empirical returned null for product_override_counts(...) even
        // with seeded fixtures + service-key; the VIEW has security_invoker
        // and PostgREST can't resolve the to-one embed. Second fetch keyed by
        // product IDs in this page; .in() with up to ~100 IDs per page is
        // well within PostgREST limits. Skip when no rows came back.
        const ids = baseRows.map((r) => r.id);
        let countsMap: Map<
          string,
          { velocity_override_count: number; price_override_count: number }
        > = new Map();
        if (ids.length > 0) {
          const { data: counts, error: cntErr } = await supabase
            .from('product_override_counts')
            .select('product_id, velocity_override_count, price_override_count')
            .in('product_id', ids);
          if (!cntErr && counts) {
            countsMap = new Map(
              (
                counts as Array<{
                  product_id: string;
                  velocity_override_count: number;
                  price_override_count: number;
                }>
              ).map((c) => [
                c.product_id,
                {
                  velocity_override_count: c.velocity_override_count,
                  price_override_count: c.price_override_count,
                },
              ])
            );
          }
        }

        const rows: ProductRow[] = baseRows.map((p) => ({
          ...p,
          _velocityOverrideCount: countsMap.get(p.id)?.velocity_override_count ?? 0,
          _priceOverrideCount: countsMap.get(p.id)?.price_override_count ?? 0,
        }));

        successCallback(rows, count || 0);
      } catch (e) {
        reportDatasourceFailure(
          'Products',
          { rpcName: 'products', params: { startRow, endRow } },
          e,
          () => failCallback()
        );
      }
    },
    [flagCounts]
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent<ProductRow>): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
      // Apply persisted column visibility — defer to allow columns to register
      setTimeout(() => {
        const toHide = Object.entries(hiddenCols)
          .filter(([, hidden]) => hidden)
          .map(([colId]) => colId);
        if (toHide.length > 0) event.api.setColumnsVisible(toHide, false);
      }, 0);
    },
    [getRowsFn, hiddenCols]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, getRowsFn]);

  // Copy ISG SKU to clipboard
  const copyToClipboard = useCallback((text: string | null): void => {
    if (text) navigator.clipboard.writeText(text);
  }, []);

  // ── Cell renderers ────────────────────────────────────────

  type RowParams = ICellRendererParams<ProductRow>;

  const SkuCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(p.data!.isg_sku);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
          title="Click to copy"
        >
          {p.data.isg_sku}
        </button>
      );
    },
    [copyToClipboard]
  );

  const ImageCell = useCallback((p: RowParams) => {
    if (!p.data?.image_url)
      return (
        <div className="w-8 h-8 rounded bg-surface-3 flex items-center justify-center">
          <Package size={12} className="text-ink-muted" />
        </div>
      );
    return (
      <img src={p.data.image_url} alt="" className="w-8 h-8 rounded object-contain bg-white" />
    );
  }, []);

  const FlagCell = useCallback(
    (p: RowParams) => {
      const count = p.data?._flagCount || 0;
      if (count === 0) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/catalog/flagged?sku=${p.data!.isg_sku}`);
          }}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-danger/15 text-danger text-2xs font-medium cursor-pointer"
        >
          <Flag size={9} /> {count}
        </button>
      );
    },
    [navigate]
  );

  // Day 84 P6.3 L11 — Overrides badge cellRenderer. Italic V / P chips render
  // when the product has a manual override on either field (counts are summed
  // across marketplaces in the VIEW). Native `title` for hover detail per the
  // same L8 grid-perf constraint as CostCell.
  const OverridesCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const v = p.data._velocityOverrideCount ?? 0;
    const pCount = p.data._priceOverrideCount ?? 0;
    if (v === 0 && pCount === 0) {
      return <span className="text-ink-muted">—</span>;
    }
    return (
      <span
        className="flex items-center gap-1 text-2xs"
        title={
          `${v} velocity override${v === 1 ? '' : 's'}` +
          ` / ${pCount} price override${pCount === 1 ? '' : 's'}`
        }
      >
        {pCount > 0 && (
          <span className="italic px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium">
            P{pCount > 1 ? `×${pCount}` : ''}
          </span>
        )}
        {v > 0 && (
          <span className="italic px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium">
            V{v > 1 ? `×${v}` : ''}
          </span>
        )}
      </span>
    );
  }, []);

  // K5.3 (F1) — clearance badge cellRenderer. Info/neutral (accent tint — NOT a risk tint; the
  // clearance badge is provenance-only per §K5, zero economic behavior). Renders only on a lot.
  const ClearanceCell = useCallback((p: RowParams) => {
    if (!p.data || p.data.is_clearance_lot !== true)
      return <span className="text-ink-muted">—</span>;
    return (
      <span
        className="px-1.5 py-0.5 rounded bg-accent/15 text-accent text-2xs font-medium"
        title="Clearance lot — cost is the clearance price (provenance only; no economic effect)"
      >
        Clearance
      </span>
    );
  }, []);

  // Day 83 P6.2 — Cost cellRenderer wrapping fmt$ in ProvenanceTooltip mode='cell'
  // (native HTML title= for grid perf, no React hover listeners per L8 + Phase 1
  // cell-mode contract). previousValueFormatter={fmt$} so override-chain
  // "Was: $X.XX" renders with currency format.
  const CostCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <ProvenanceTooltip
          provenance={p.data.provenance}
          fieldKey="cost_usd"
          staleThresholdDays={staleDays}
          mode="cell"
          previousValueFormatter={(v) => fmt$(typeof v === 'number' ? v : null)}
        >
          {fmt$(p.data.cost_usd)}
        </ProvenanceTooltip>
      );
    },
    [staleDays]
  );

  // ── Column definitions ────────────────────────────────────

  const dashIfEmpty = (p: ValueFormatterParams<ProductRow>): string =>
    p.value === null || p.value === undefined || p.value === '' ? '\u2014' : String(p.value);

  const columnDefs = useMemo<ColDef<ProductRow>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        width: 120,
        pinned: 'left',
        suppressMovable: true,
        resizable: false,
        cellRenderer: SkuCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: '',
        field: 'image_url',
        width: 50,
        sortable: false,
        filter: false,
        cellRenderer: ImageCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        } as CellStyle,
      },
      {
        headerName: 'Title',
        field: 'title',
        flex: 2,
        minWidth: 200,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
          color: 'var(--ink-bright)',
        } as CellStyle,
      },
      {
        headerName: 'Brand',
        field: 'brand',
        width: 120,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'Category',
        field: 'category_algopix',
        width: 140,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'UPC',
        field: 'upc',
        width: 130,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-muted)',
        } as CellStyle,
      },
      {
        headerName: 'ASIN',
        field: 'asin',
        width: 120,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-muted)',
        } as CellStyle,
      },
      {
        headerName: 'Cost',
        field: 'cost_usd',
        width: 80,
        // U10 cost re-point — junction-assembled value; ORDER BY can't push to
        // the server post-assembly (the read-copy column dies at slice B).
        sortable: false,
        cellRenderer: CostCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Buy Box',
        field: 'amazon_buy_box_price',
        width: 90,
        valueFormatter: (p: ValueFormatterParams<ProductRow>) => fmt$(p.value as number | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Demand',
        field: 'amazon_demand_rating',
        width: 80,
        valueFormatter: dashIfEmpty,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Mo. Sales',
        field: 'amazon_monthly_sales',
        width: 90,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Sellers',
        field: 'amazon_seller_count',
        width: 75,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Buyer',
        field: 'assigned_buyer_full_name',
        width: 130,
        // Sort/filter disabled — both would require server-side ORDER BY /
        // WHERE on the flattened embed, which PostgREST doesn't translate
        // cleanly. ProductDetail editor is the canonical assignment surface.
        sortable: false,
        filter: false,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'Flagged',
        colId: '_flags',
        width: 75,
        sortable: false,
        filter: false,
        cellRenderer: FlagCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Overrides',
        colId: '_overrides',
        width: 95,
        sortable: false,
        filter: false,
        cellRenderer: OverridesCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Clearance',
        colId: '_clearance',
        width: 95,
        sortable: false,
        filter: false,
        cellRenderer: ClearanceCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Updated',
        field: 'updated_at',
        width: 90,
        valueFormatter: (p: ValueFormatterParams<ProductRow>) =>
          relativeTime(p.value as string | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          color: 'var(--ink-muted)',
        } as CellStyle,
      },
    ],
    [SkuCell, ImageCell, FlagCell, CostCell, OverridesCell, ClearanceCell]
  );

  const defaultColDef = useMemo<ColDef<ProductRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<ProductRow>): void => {
      if (!event.data) return;
      navigate(`/catalog/products/${event.data.isg_sku}`);
    },
    [navigate]
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-base font-semibold text-ink-bright">Products</h1>
          <div className="flex items-center gap-2">
            {/* Column chooser */}
            <div className="relative">
              <button
                onClick={() => (colChooserOpen ? closeColChooser() : openColChooser())}
                className="arbi-btn-ghost text-xs px-2 py-1"
              >
                <Columns3 size={12} /> Columns
              </button>
              {colChooserOpen && (
                <>
                  {/* Backdrop for outside-click dismissal */}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={closeColChooser}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-surface-2 border border-border rounded-lg shadow-xl py-1 w-44">
                    {CHOOSABLE_COLUMNS.map((col) => (
                      <button
                        key={col.colId}
                        onClick={() => toggleColVisibility(col.colId)}
                        className="flex items-start gap-2 w-full px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
                      >
                        <span
                          className={clsx(
                            'w-3.5 h-3.5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0',
                            hiddenCols[col.colId]
                              ? 'border-border bg-surface-3'
                              : 'border-accent bg-accent'
                          )}
                        >
                          {!hiddenCols[col.colId] && <Check size={10} className="text-white" />}
                        </span>
                        <span className="flex flex-col items-start min-w-0">
                          <span>{col.label}</span>
                          {col.colId === 'cost_usd' && costSourceSubtitle && (
                            <span className="text-2xs text-ink-muted">{costSourceSubtitle}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-products" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Flag filter — LEFT */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {(
              [
                { id: 'all', label: 'All' },
                { id: 'flagged', label: 'Flagged' },
                { id: 'unflagged', label: 'Clean' },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                onClick={() => setFlagFilter(f.id)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer',
                  flagFilter === f.id
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Search — RIGHT */}
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU, title, brand, UPC, ASIN..."
              aria-label="Search products"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact<ProductRow>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={200}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          overlayNoRowsTemplate='<span style="color:var(--ink-muted);font-size:13px">No products yet — upload an Algopix file in Files to get started</span>'
        />
      </div>
    </div>
  );
}
